import React from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';
import styled from 'styled-components';
import HotelsTablePagination from './PaginationComponent';
import Loader from '../Loader';
import { NoHotelsText } from '../HotelsTable/HotelsTable.styled';
// Styled Components
const TableContainer = styled.div<{ $height?: string }>`
  display: flex;
  flex-direction: column;
  height: ${({ $height }) => $height || '100%'};
  width: 100%;
  border: 1px solid var(--color-gray-200);
  border-radius: var(--radius-lg);
  background-color: var(--color-white);
  overflow: hidden;
`;
const TableWrapper = styled.div`
  flex: 1;
  // min-height: 300px;
  overflow: auto;
  position: relative;
  // -webkit-overflow-scrolling: touch;
  scrollbar-gutter: stable;
`;
const LoaderOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.8);
  z-index: 20;
`;
const StyledTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;
`;
const StickyHeader = styled.thead`
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: var(--color-gray-50);
  tr {
    background-color: var(--color-gray-50);
  }
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
  }
`;
const TableHeaderCell = styled.th<{
  $align?: 'left' | 'center' | 'right';
  $sortable?: boolean;
  $width?: string | number;
  $hideHeaderSeparators?: boolean;
}>`
  padding: var(--spacing-4);
  font-weight: var(--font-weight-semibold);
  color: var(--color-gray-700);
  text-transform: uppercase;
  font-size: var(--font-size-14);
  letter-spacing: var(--letter-spacing-normal);
  text-align: ${({ $align }) => $align || 'left'};
  white-space: nowrap;
  background-color: transparent;
  position: relative;
  width: ${({ $width }) =>
    $width ? (typeof $width === 'number' ? `${$width}px` : $width) : 'auto'};
  ${({ $hideHeaderSeparators }) =>
    !$hideHeaderSeparators &&
    `
    &:not(:last-child)::after {
      content: '';
      position: absolute;
      right: 0;
      top: 25%;
      bottom: 25%;
      width: 1px;
      background-color: var(--color-gray-300);
    }
  `}
  ${({ $sortable }) =>
    $sortable &&
    `
    cursor: pointer;
    user-select: none;
    transition: var(--transition-fast);
  `}
`;
const HeaderContent = styled.div<{ $align?: 'left' | 'center' | 'right' }>`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--spacing-2);
  width: 100%;
  
  span {
    flex: 1;
    text-align: ${({ $align }) => $align || 'left'};
  }
`;
const SortIcon = styled.div<{ $direction?: 'asc' | 'desc' | null }>`
  display: flex;
  align-items: center;
  font-size: var(--font-size-14);
  .sort-arrows {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0;
    line-height: 1;
  }
  .arrow-up,
  .arrow-down {
    color: var(--color-gray-800);
    transition: color 0.2s ease;
    line-height: 1;
    margin: -2px 0;
    opacity: 0.4;
  }
  .arrow-up.active {
    color: var(--color-primary-600);
    opacity: 1;
    font-weight: bold;
  }
  .arrow-down.active {
    color: var(--color-primary-600);
    opacity: 1;
    font-weight: bold;
  }
  ${({ $direction }) =>
    !$direction &&
    `
    .arrow-up,
    .arrow-down {
      color: var(--color-gray-600);
      opacity: 0.5;
    }
  `}
`;
const TableBody = styled.tbody`
  background-color: var(--color-white);
`;
const TableRow = styled.tr<{ $striped?: boolean; $hoverable?: boolean }>`
  transition: var(--transition-fast);
  border-bottom: 1px solid var(--color-gray-100);
  ${({ $striped }) =>
    $striped &&
    `
    &:nth-child(even) {
      background-color: var(--color-gray-25);
    }
  `}
  ${({ $hoverable }) =>
    $hoverable &&
    `
    &:hover {
      background-color: var(--color-planet-green-light, #D4F4DD) !important;
    }
  `}
`;
const TableCell = styled.td<{ $align?: 'left' | 'center' | 'right' }>`
  padding: var(--spacing-5) var(--spacing-4);
  vertical-align: middle;
  text-align: ${({ $align }) => $align || 'left'};
  word-wrap: break-word;
  overflow-wrap: break-word;
`;
const PaginationWrapper = styled.div`
  border-top: 1px solid var(--color-gray-200);
  padding: var(--spacing-4);
  background-color: var(--color-white);
`;
const EmptyStateWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  width: 100%;
`;
const LoadingMoreRow = styled.tr`
  background-color: var(--color-white);
  td {
    padding: var(--spacing-4);
    text-align: center;
    color: var(--color-gray-600);
    font-size: var(--font-size-14);
    font-style: italic;
  }
`;
// Column definition interface
export interface ColumnDef<T = any> {
  key: string;
  label: string;
  align?: 'left' | 'center' | 'right';
  sortable?: boolean;
  render?: (item: T, index: number) => React.ReactNode;
  headerRender?: () => React.ReactNode;
  className?: string;
  width?: string | number;
}
// Pagination props
export interface PaginationConfig {
  currentPage: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
  onPrevPage: () => void;
  onNextPage: () => void;
  onPageChange?: (page: number) => void;
  itemsPerPage?: number;
  totalCount?: number;
  onPageSizeChange?: (pageSize: number) => void;
  pageSizeOptions?: number[];
}
// TableWithPagination Props
export interface TableWithPaginationProps<T = any> {
  columns: ColumnDef<T>[];
  data: T[];
  pagination: PaginationConfig;
  loading?: boolean;
  loadingMessage?: string;
  emptyMessage?: string;
  height?: string;
  striped?: boolean;
  sortKey?: string;
  sortOrder?: 'asc' | 'desc' | null;
  onSort?: (key: string) => void;
  getRowKey?: (item: T, index: number) => string | number;
  onRowClick?: (item: T, index: number) => void;
  onHoverRow?: (item: T, index: number) => void;
  className?: string;
  showPagination?: boolean;
  isLoadingMore?: boolean;
  hideHeaderSeparators?: boolean;
}

/**
 * TableWithPagination Component
 *
 * A table component with fixed header, scrollable body, and pagination at the bottom.
 *
 * @example
 * <TableWithPagination
 *   columns={columns}
 *   data={data}
 *   height="600px"
 *   pagination={{
 *     currentPage: 1,
 *     totalPages: 10,
 *     hasNextPage: true,
 *     hasPrevPage: false,
 *     onPrevPage: () => {},
 *     onNextPage: () => {},
 *     totalCount: 100,
 *     itemsPerPage: 10
 *   }}
 *   sortKey="name"
 *   sortOrder="asc"
 *   onSort={handleSort}
 * />
 */
export const TableWithPagination = <T extends Record<string, any>>({
  columns,
  data,
  pagination,
  loading = false,
  loadingMessage = 'Loading data...',
  emptyMessage = 'No data available',
  height = '100%',
  striped = true,
  sortKey,
  sortOrder,
  onSort,
  getRowKey,
  onRowClick,
  onHoverRow,
  className,
  showPagination = true,
  isLoadingMore = false,
  hideHeaderSeparators = false,
}: TableWithPaginationProps<T>) => {
  // Default row key generator
  const defaultGetRowKey = (item: T, index: number): string | number => {
    if (item.id !== undefined) return item.id;
    if (item._id !== undefined) return item._id;
    return index;
  };

  const rowKeyGetter = getRowKey || defaultGetRowKey;

  // Handle sort
  const handleSort = (columnKey: string) => {
    if (onSort) {
      onSort(columnKey);
    }
  };

  return (
    <TableContainer $height={height} className={className}>
      <TableWrapper className="custom-scrollbar">
        {/* Loading Overlay */}
        {loading && (
          <LoaderOverlay>
            <Loader spinnerClassName="w-9 h-9" message={loadingMessage} />
          </LoaderOverlay>
        )}

        <StyledTable>
          <colgroup>
            {columns.map((col, index) => (
              <col
                key={index}
                style={col.width ? { width: col.width } : undefined}
              />
            ))}
          </colgroup>

          {/* Fixed Header */}
          <StickyHeader>
            <tr>
              {columns.map(column => (
                <TableHeaderCell
                  key={column.key}
                  $align={column.align || 'left'}
                  $sortable={column.sortable}
                  $width={column.width}
                  $hideHeaderSeparators={hideHeaderSeparators}
                  onClick={
                    column.sortable ? () => handleSort(column.key) : undefined
                  }
                  className={column.className}
                >
                  {column.sortable ? (
                    <HeaderContent $align={column.align}>
                      <span>
                        {column.headerRender
                          ? column.headerRender()
                          : column.label}
                      </span>
                      <SortIcon
                        $direction={sortKey === column.key ? sortOrder : null}
                      >
                        <div className="sort-arrows">
                          <ChevronUp
                            size={16}
                            className={`arrow-up ${
                              sortKey === column.key && sortOrder === 'asc'
                                ? 'active'
                                : ''
                            }`}
                            strokeWidth={2}
                          />
                          <ChevronDown
                            size={16}
                            className={`arrow-down ${
                              sortKey === column.key && sortOrder === 'desc'
                                ? 'active'
                                : ''
                            }`}
                            strokeWidth={2}
                          />
                        </div>
                      </SortIcon>
                    </HeaderContent>
                  ) : column.headerRender ? (
                    column.headerRender()
                  ) : (
                    column.label
                  )}
                </TableHeaderCell>
              ))}
            </tr>
          </StickyHeader>

          {/* Table Body */}
          <TableBody>
            {!loading && data && data.length > 0
              ? data.map((item, index) => (
                  <TableRow
                    key={rowKeyGetter(item, index)}
                    $striped={striped}
                    $hoverable={!!onHoverRow}
                    onClick={
                      onRowClick ? () => onRowClick(item, index) : undefined
                    }
                    onMouseEnter={
                      onHoverRow ? () => onHoverRow(item, index) : undefined
                    }
                    style={onRowClick ? { cursor: 'pointer' } : undefined}
                  >
                    {columns.map(column => (
                      <TableCell
                        key={column.key}
                        $align={column.align || 'left'}
                        className={column.className}
                      >
                        {column.render
                          ? column.render(item, index)
                          : item[column.key] !== undefined
                          ? item[column.key]
                          : '-'}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : null}
            
            {/* Loading More Indicator */}
            {!loading && isLoadingMore && data && data.length > 0 && (
              <LoadingMoreRow>
                <td colSpan={columns.length}>
                  Loading more data...
                </td>
              </LoadingMoreRow>
            )}
          </TableBody>
        </StyledTable>

        {/* Empty State - Centered in middle of table */}
        {!loading && (!data || data.length === 0) && (
          <EmptyStateWrapper>
            <NoHotelsText>{emptyMessage}</NoHotelsText>
          </EmptyStateWrapper>
        )}
      </TableWrapper>

      {/* Pagination at Bottom - Only show when there's data and showPagination is true */}
      {showPagination && data && data.length > 0 && (
        <PaginationWrapper>
          <HotelsTablePagination
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            hasNextPage={pagination.hasNextPage}
            hasPrevPage={pagination.hasPrevPage}
            onPrevPage={pagination.onPrevPage}
            onNextPage={pagination.onNextPage}
            onPageChange={pagination.onPageChange}
            itemsPerPage={pagination.itemsPerPage}
            totalCount={pagination.totalCount}
            disabled={loading || isLoadingMore}
            onPageSizeChange={pagination.onPageSizeChange}
            pageSizeOptions={pagination.pageSizeOptions}
          />
        </PaginationWrapper>
      )}
    </TableContainer>
  );
};

export default TableWithPagination;
